import { useMemo } from "react";
import { usePilotageRhStore } from "../store/usePilotageRhStore";
import { computeMetrics } from "../functions/compute";
import { mapEffectif } from "../functions/mapEffectif";
import { mapDiversite } from "../functions/mapDiversite";
import { mapRetention } from "../functions/mapRetention";
import { mapMouvements } from "../functions/mapMouvements";
import { mapAbsenteisme } from "../functions/mapAbsenteisme";
import { mapTempsTravail } from "../functions/mapTempsTravail";
import { mapFrais } from "../functions/mapFrais";
import { mapTaches } from "../functions/mapTaches";
import {
    useQueryPilotageAbsenteisme,
    useQueryPilotageDiversite,
    useQueryPilotageEffectif,
    useQueryPilotageFrais,
    useQueryPilotageMouvements,
    useQueryPilotageRetention,
    useQueryPilotageTaches,
    useQueryPilotageTempsTravail,
} from "../api/queries";

/** Mappe la réponse d'une query de section vers le modèle de la section */
const useSectionData = (query, mapFn) => {
    const data = useMemo(
        () => (query.data ? mapFn(query.data) : null),
        [query.data, mapFn]
    );
    return {
        data,
        isLoading: query.isLoading,
        isFetching: query.isFetching,
    };
};

/**
 * Logique de la page :
 *  - Effectif, Diversité, Rétention, Mouvements, Absentéisme, Temps de travail,
 *    Notes de frais, Tâches & Suivi : données réelles (API)
 *  - autres sections : données de démonstration (computeMetrics) en attendant leurs endpoints
 */
const usePilotageRh = () => {
    const filters = usePilotageRhStore((s) => s.filters);
    const resetFilters = usePilotageRhStore((s) => s.resetFilters);

    const effectif = useSectionData(
        useQueryPilotageEffectif(filters),
        mapEffectif
    );
    const diversite = useSectionData(
        useQueryPilotageDiversite(filters),
        mapDiversite
    );
    const retention = useSectionData(
        useQueryPilotageRetention(filters),
        mapRetention
    );
    const mouvements = useSectionData(
        useQueryPilotageMouvements(filters),
        mapMouvements
    );
    const absences = useSectionData(
        useQueryPilotageAbsenteisme(filters),
        mapAbsenteisme
    );
    const temps = useSectionData(
        useQueryPilotageTempsTravail(filters),
        mapTempsTravail
    );
    const frais = useSectionData(useQueryPilotageFrais(filters), mapFrais);
    const taches = useSectionData(useQueryPilotageTaches(filters), mapTaches);

    const metrics = useMemo(() => computeMetrics(filters), [filters]);

    const sections = [
        effectif,
        diversite,
        retention,
        mouvements,
        absences,
        temps,
        frais,
        taches,
    ];

    return {
        filters,
        effectif,
        diversite,
        retention,
        mouvements,
        absences,
        temps,
        frais,
        taches,
        isFetching: sections.some((s) => s.isFetching),
        metrics,
        resetFilters,
    };
};

export default usePilotageRh;
